import { useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { addSnackbarMessage } from '~/actions/snackbar.actions';
import { useBarcode } from '~/modules/devices/hooks/useBarcode';
import { useScanToPrint } from '~/modules/print/hooks/useScanToPrint';
import { PrintAssetType } from '~/modules/print/types/print.type';
import { getUserHasFullAccess, getUserPlanIsLoaded } from '~/selectors/plans.selectors';
import { newRelic } from '~/utils/newRelic/newRelic';

import { SCAN_EVENT_QUEUE } from '../constants/scanEventQueue.const';
import { createScanActionFailureScanAction } from '../scanActions/scanActionFailure.action';
import { createScanBarcodeContent } from '../utils/createScanBarcodeContent.util';
import { createScanContext } from '../utils/createScanContext.util';
import { createScanEventProcessor } from '../utils/createScanEventProcessor.util';
import { createScanActionHandler } from '../utils/scanActionHandler.util';

/**
 * Mounts the sole consumer of the scan event queue.
 */
export const useScanEventProcessor = () => {
  const dispatch = useDispatch();
  const { resolveBarcode } = useBarcode();
  const hasFullAccess = useSelector(getUserHasFullAccess);
  const isPlanLoaded = useSelector(getUserPlanIsLoaded);
  const { isConfiguredForScanToPrint } = useScanToPrint(PrintAssetType.ShippingLabel);
  const hasFullAccessRef = useRef(hasFullAccess);
  hasFullAccessRef.current = hasFullAccess;
  const isScanToPrintEnabledRef = useRef(isConfiguredForScanToPrint);
  isScanToPrintEnabledRef.current = isConfiguredForScanToPrint;

  useEffect(() => {
    if (!isPlanLoaded) return;

    const scanActionHandler = createScanActionHandler({
      failureAction: createScanActionFailureScanAction({
        isScanToPrintEnabled: () => isScanToPrintEnabledRef.current,
        notifyError: () => {
          dispatch(
            addSnackbarMessage({
              i18n: true,
              message: 'orders:clickToPrint.scanToPrint.unassociatedBarcode',
              severity: 'error',
            }),
          );
        },
      }),
    });

    const processor = createScanEventProcessor({
      canProcessScanEvent: () => hasFullAccessRef.current,
      getScanContext: (source) =>
        createScanContext({
          ...source,
          getBarcodeContent: (barcodeSource) =>
            createScanBarcodeContent({ ...barcodeSource, resolveBarcode }),
          getIsConfiguredForScanToPrint: () => isScanToPrintEnabledRef.current,
        }),
      handleScanEvent: scanActionHandler.handle,
      queue: SCAN_EVENT_QUEUE,
    });

    return SCAN_EVENT_QUEUE.subscribe(() => {
      processor
        .process()
        .catch((error) => newRelic.noticeError('Failed to drain the scan event queue', { error }));
    });
  }, [dispatch, isPlanLoaded, resolveBarcode]);
};
